import { MapPin } from "lucide-react";
import { useLiveEvents, realtimePages } from "@/mock";
import { LiveDot } from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useRealtime } from "@/data/hooks";

/** Verbs for rendering real realtime events (which carry a type + path). */
function verbFor(type: string): string {
  const t = type.toLowerCase();
  if (t.includes("pageview") || t === "$pageview") return "viewed";
  if (t.includes("click")) return "clicked on";
  if (t.includes("submit") || t.includes("form")) return "submitted on";
  if (t.includes("scroll")) return "scrolled";
  if (t.includes("convert")) return "converted on";
  return "active on";
}

/**
 * Live mini event stream — newest fading in at the top.
 *
 * LIVE: backend `realtime.recent[]` ({ts,type,path}) rendered with a derived
 * verb. SAMPLE: the simulated city/verb/page ticker. The provenance chip
 * always reflects which source is on screen.
 */
export function LiveStream() {
  const { siteId, live: wsLive } = useWorkspace();
  const realtimeQ = useRealtime(siteId);

  const sampleEvents = useLiveEvents(7);
  const top = realtimePages[0]!;

  const recent = realtimeQ.data?.recent ?? [];
  const isLive = wsLive && !!siteId && !realtimeQ.isError && recent.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LiveDot />
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">Live right now</h3>
        </div>
        <span className="font-mono text-[10px] lowercase tracking-wide text-subtle">
          {isLive ? "live" : "sample"}
        </span>
      </div>

      {isLive ? (
        <ul className="-mt-0.5 flex-1 space-y-0.5">
          {recent.slice(0, 7).map((e, i) => (
            <li
              key={`${e.ts}-${i}`}
              className="flex items-center gap-2 rounded-btn px-2 py-1.5 text-[13px] animate-fade-up"
              style={{ opacity: i === 0 ? 1 : Math.max(0.45, 1 - i * 0.09) }}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={1.5} />
              <span className="truncate text-muted">{verbFor(e.type)}</span>
              <span className="ml-auto shrink-0 font-mono text-[11px] text-subtle">{e.path}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="-mt-0.5 flex-1 space-y-0.5">
          {sampleEvents.map((e, i) => (
            <li
              key={e.id}
              className="flex items-center gap-2 rounded-btn px-2 py-1.5 text-[13px] animate-fade-up"
              style={{ opacity: i === 0 ? 1 : Math.max(0.45, 1 - i * 0.09) }}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={1.5} />
              <span className="shrink-0 font-medium text-ink-strong">{e.city}</span>
              <span className="truncate text-muted">{e.label}</span>
              <span className="ml-auto shrink-0 font-mono text-[11px] text-subtle">{e.page}</span>
            </li>
          ))}
        </ul>
      )}

      {!isLive && (
        <div className="mt-3 border-t border-hairline pt-3">
          <p className="eyebrow mb-1.5">Busiest page</p>
          <div className="flex items-baseline justify-between text-[13px]">
            <span className="font-mono text-muted">{top.label}</span>
            <span className="font-mono text-[12px] text-ink-strong">{top.value} reading</span>
          </div>
        </div>
      )}
    </div>
  );
}
